import { faCheck, faChevronLeft, faMoneyBillTransfer, faNoteSticky, faGift, faCalendarDays, faGear, faCircle, faShoppingBag, faCoins, faRotateLeft, faUserGroup, faLock, faLockOpen, faBan, faTrash, faComment, faReply, faUsers, faCircleCheck, faCircleXmark, faClock, faLink, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import authApi from "../../api/authApi";
import { authActions } from "../../store/authSlice";
import NotificationPrompt from "../../components/PushNotification/NotificationPrompt";
import openUtilityBlockInNewTab from "../../utils/open-utility-block-in-new-tab";
import { formatTimeAgo, formatFullDateTime } from "../../utils/formatTimeAgo";

// Mapping notification types to tab categories
const TYPE_TO_TAB = {
    payment: "payment",
    withdraw_requested: "payment",
    withdraw_processed: "payment",
    withdraw_cancelled: "payment",
    bonus: "bonus",
    cash_added: "bonus",
    cash_deducted: "bonus",
    fraud_deduction: "bonus",
    ref: "bonus",
    voucher_redeemed: "bonus",
    VOUCHER_REDEEMED: "bonus",
    event: "event",
    comment: "event",
    reply: "event",
    post_comment: "event",
    post_reply: "event",
    // Kênh cộng đồng: deal mới + kết quả duyệt bài (tác giả) + hàng chờ (admin/mod)
    community_post: "event",
    community_post_approved: "event",
    community_post_rejected: "event",
    community_post_pending: "event",
    // Tiện ích (site ngoài xử lý theo lô rồi báo về)
    utility_task_ready: "event",
    UTILITY_TASK_READY: "event",
    utility_task_failed: "event",
    UTILITY_TASK_FAILED: "event",
    utility_task_requested: "event",
    UTILITY_TASK_REQUESTED: "event",
    system: "system",
    order_received: "system",
    order_completed: "system",
    refund_processed: "system",
    account_locked: "system",
    account_unlocked: "system",
};

const TYPE_LABELS = {
    payment: "Thanh toán",
    bonus: "Tiền thưởng",
    event: "Sự kiện",
    system: "Hệ thống",
    order_received: "Đơn hàng",
    order_completed: "Đơn hàng",
    cash_added: "Tiền",
    cash_deducted: "Tiền",
    refund_processed: "Đơn hàng",
    withdraw_requested: "Thanh toán",
    withdraw_processed: "Thanh toán",
    withdraw_cancelled: "Thanh toán",
    fraud_deduction: "Gian lận",
    ref: "Giới thiệu",
    account_locked: "Tài khoản",
    account_unlocked: "Tài khoản",
    comment: "Bình luận",
    reply: "Trả lời",
    post_comment: "Bình luận",
    post_reply: "Trả lời",
};

const TYPE_ICONS = {
    payment: faMoneyBillTransfer,
    bonus: faGift,
    event: faCalendarDays,
    system: faGear,
    order_received: faShoppingBag,
    order_completed: faShoppingBag,
    cash_added: faCoins,
    cash_deducted: faCoins,
    refund_processed: faRotateLeft,
    withdraw_requested: faMoneyBillTransfer,
    withdraw_processed: faMoneyBillTransfer,
    withdraw_cancelled: faMoneyBillTransfer,
    fraud_deduction: faBan,
    ref: faUserGroup,
    account_locked: faLock,
    account_unlocked: faLockOpen,
    voucher_redeemed: faGift,
    VOUCHER_REDEEMED: faGift,
    comment: faComment,
    reply: faReply,
    post_comment: faComment,
    post_reply: faReply,
    community_post: faUsers,
    community_post_approved: faCircleCheck,
    community_post_rejected: faCircleXmark,
    community_post_pending: faClock,
    utility_task_ready: faLink,
    UTILITY_TASK_READY: faLink,
    utility_task_failed: faTriangleExclamation,
    UTILITY_TASK_FAILED: faTriangleExclamation,
    utility_task_requested: faClock,
    UTILITY_TASK_REQUESTED: faClock,
};

const TYPE_COLORS = {
    payment: "text-green-600",
    bonus: "text-amber-600",
    event: "text-blue-600",
    system: "text-content-2",
    order_received: "text-blue-600",
    order_completed: "text-green-600",
    cash_added: "text-green-600",
    cash_deducted: "text-red-600",
    refund_processed: "text-orange-600",
    withdraw_requested: "text-green-600",
    withdraw_processed: "text-green-600",
    withdraw_cancelled: "text-red-600",
    fraud_deduction: "text-red-600",
    ref: "text-purple-600",
    account_locked: "text-red-600",
    account_unlocked: "text-green-600",
    voucher_redeemed: "text-green-600",
    VOUCHER_REDEEMED: "text-green-600",
    comment: "text-blue-600",
    reply: "text-indigo-600",
    post_comment: "text-blue-600",
    post_reply: "text-indigo-600",
    community_post: "text-blue-600",
    community_post_approved: "text-green-600",
    community_post_rejected: "text-red-600",
    community_post_pending: "text-amber-600",
    utility_task_ready: "text-green-600",
    UTILITY_TASK_READY: "text-green-600",
    utility_task_failed: "text-red-600",
    UTILITY_TASK_FAILED: "text-red-600",
    utility_task_requested: "text-amber-600",
    UTILITY_TASK_REQUESTED: "text-amber-600",
};

// Order-related notification types that should navigate to order page
const ORDER_NOTIFICATION_TYPES = ["order_received", "order_completed", "refund_processed"];

const ROUTE_BY_TYPE = {
    payment: "/user-info/withdraw",
    // Noti cộng/trừ tiền → mở sổ số dư (giải thích khoản tiền), không phải màn rút tiền
    bonus: "/user-info/cash-history",
    withdraw_requested: "/user-info/withdraw",
    withdraw_processed: "/user-info/withdraw",
    withdraw_cancelled: "/user-info/withdraw",
    voucher_redeemed: "/voucher/my-vouchers",
    VOUCHER_REDEEMED: "/voucher/my-vouchers",
    comment: "/post",
    reply: "/post",
};

const TABS = [
    { key: "all", label: "Tất cả" },
    { key: "payment", label: "Thanh toán" },
    { key: "bonus", label: "Tiền thưởng" },
    { key: "event", label: "Sự kiện" },
    { key: "system", label: "Hệ thống" },
];

// Helper to convert createTime to milliseconds (handle both seconds and milliseconds for backward compatibility)
const getTimeInMs = (createTime) => {
    if (!createTime) return 0;
    // If createTime is in seconds (less than year 2000 in ms), multiply by 1000
    if (createTime < 946684800000) {
        return createTime * 1000;
    }
    return createTime;
};

function Notification() {
    const { userInfo } = useSelector((state) => state.auth);
    const notifications = userInfo?.notifications || [];
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = React.useState("all");
    const [marking, setMarking] = React.useState(false);
    const [deleting, setDeleting] = React.useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);

    // refs cho từng tab
    const tabRefs = React.useRef({});

    // auto scroll tới tab active
    React.useEffect(() => {
        const el = tabRefs.current[activeTab];
        el?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }, [activeTab]);

    // Sau mỗi thao tác chỉ tải lại DANH SÁCH THÔNG BÁO (1 truy vấn) rồi vá vào
    // userInfo, thay vì gọi get-me (6 truy vấn) để lấy lại cả hồ sơ.
    const refreshNotifications = React.useCallback(async () => {
        const { data } = await authApi.myNotifications();
        dispatch(authActions.setNotifications(data.data.notifications || []));
    }, [dispatch]);

    const onReadAll = React.useCallback(async () => {
        try {
            setMarking(true);
            await authApi.readAllNoti();
            await refreshNotifications();
        } catch (error) {
            console.error(error);
        } finally {
            setMarking(false);
        }
    }, [refreshNotifications]);

    const onDeleteOne = React.useCallback(async (id) => {
        try {
            await authApi.deleteNotification(id);
            await refreshNotifications();
        } catch (error) {
            console.error(error);
        }
    }, [refreshNotifications]);

    const onDeleteAll = React.useCallback(async () => {
        try {
            setDeleting(true);
            await authApi.deleteAllNotifications();
            await refreshNotifications();
            setShowDeleteConfirm(false);
        } catch (error) {
            console.error(error);
        } finally {
            setDeleting(false);
        }
    }, [refreshNotifications]);

    const unreadCountByType = React.useMemo(() => {
        const all = notifications.filter((n) => n.unread).length;
        // Count by notification type
        const byType = notifications.reduce((acc, n) => {
            if (n.unread) acc[n.type] = (acc[n.type] || 0) + 1;
            return acc;
        }, {});
        // Count by tab category
        const byTab = notifications.reduce((acc, n) => {
            if (n.unread) {
                const tab = TYPE_TO_TAB[n.type] || "system";
                acc[tab] = (acc[tab] || 0) + 1;
            }
            return acc;
        }, {});
        return { all, ...byTab, ...byType }; // { all: 3, payment:2, bonus:1, ... }
    }, [notifications]);

    // Sort mới nhất trước (nếu backend chưa sort)
    const sorted = React.useMemo(() => {
        return [...notifications].sort((a, b) => {
            const ta = getTimeInMs(a.createTime);
            const tb = getTimeInMs(b.createTime);
            return tb - ta;
        });
    }, [notifications]);

    const filtered = React.useMemo(() => {
        if (activeTab === "all") return sorted;
        // Filter by tab category - include all notification types that belong to this tab
        return sorted.filter((n) => {
            const tab = TYPE_TO_TAB[n.type] || "system";
            return tab === activeTab;
        });
    }, [sorted, activeTab]);

    const handleItemClick = React.useCallback(
        (noti) => {
            // Tiện ích chỉ có màn riêng trong app (UtilityWebView). Trên web không có
            // route /tien-ich/block/:id -> tra link block trong cấu hình "Tiện ích App"
            // rồi mở ở tab mới. Link KHÔNG nằm trong thông báo (xem openUtilityBlockById
            // bên mobile để biết vì sao).
            const blockMatch = noti?.link?.match(/^\/tien-ich\/block\/(\d+)/);
            if (blockMatch) {
                openUtilityBlockInNewTab(blockMatch[1], noti.link, userInfo);
                return;
            }
            // Ưu tiên deep-link cụ thể (vd bình luận: /post/12?comment=34)
            if (noti?.link) {
                navigate(noti.link);
                return;
            }
            // Check if it's an order-related notification
            if (ORDER_NOTIFICATION_TYPES.includes(noti?.type)) {
                navigate("/user-info/order");
                return;
            }
            // Check other route mappings
            const route = ROUTE_BY_TYPE[noti?.type];
            if (route) navigate(route);
        },
        [navigate, userInfo]
    );

    const unreadExists = React.useMemo(() => notifications.some((el) => el.unread), [notifications]);

    return (
        <div className="h-dvh flex flex-col w-full md:max-w-[768px] lg:max-w-[1024px] md:mx-auto">
            <div className="flex-1 overflow-auto">
                <div className="relative p-3 flex justify-center items-center border-b shadow-sm">
                    <h2 className="text-center font-semibold">Thông báo</h2>
                    <Link className="absolute left-2 p-2 hover:bg-surface-raised rounded-lg" to={"/"}>
                        <FontAwesomeIcon icon={faChevronLeft} className="pointer-events-none" />
                    </Link>
                </div>
                <NotificationPrompt />
                <div className="text-center text-sm py-2 bg-surface-raised relative">
                    <p>Thông báo sẽ hết hạn sau 30 ngày</p>
                </div>
                {/* Tabs */}
                <div className="px-2 py-2 border-b bg-surface">
                    <div className="flex gap-2 overflow-x-auto no-scrollbar snap-x snap-mandatory" role="tablist" aria-label="Bộ lọc thông báo">
                        {TABS.map((t) => {
                            const countUnread = t.key === "all" ? unreadCountByType.all || 0 : unreadCountByType[t.key] || 0;

                            return (
                                <button
                                    key={t.key}
                                    ref={(el) => (tabRefs.current[t.key] = el)}
                                    role="tab"
                                    aria-selected={activeTab === t.key}
                                    onClick={() => setActiveTab(t.key)}
                                    className={`flex items-center gap-1 whitespace-nowrap text-sm px-3 py-1 rounded-full border snap-start
                                    ${activeTab === t.key ? "bg-black text-white border-black" : "bg-surface hover:bg-surface-muted"}`}
                                >
                                    {t.label}
                                    {countUnread > 0 && <span className="text-xs font-medium bg-red-500 text-white rounded-full px-2">{countUnread}</span>}
                                </button>
                            );
                        })}
                    </div>
                </div>
                {/* List */}
                {filtered.length > 0 && (
                    <div className="p-2 flex flex-col gap-1">
                        {filtered.map((noti) => (
                            <NotiItem key={noti.id || `${noti.createTime}-${noti.title}`} noti={noti} onClick={handleItemClick} onDelete={onDeleteOne} />
                        ))}
                    </div>
                )}

                {/* Empty */}
                {filtered.length === 0 && (
                    <div className="flex-1 flex items-center p-4 flex-col text-content-3">
                        <FontAwesomeIcon icon={faNoteSticky} className="text-4xl text-content-3" />
                        <span>Không có thông báo trong mục này</span>
                    </div>
                )}
            </div>
            {/* Action buttons - dính ở cuối */}
            <div className="flex border-t">
                {unreadExists && (
                    <div onClick={onReadAll} disabled={marking} className={`flex-1 flex justify-center items-center gap-2 py-2 ${marking ? "bg-primary-100 cursor-wait" : "bg-primary-200 cursor-pointer hover:bg-primary-300"}`}>
                        <span className="text-sm">{marking ? "Đang đánh dấu…" : "Đã đọc tất cả"}</span>
                        <FontAwesomeIcon icon={faCheck} />
                    </div>
                )}
                {notifications.length > 0 && (
                    <div onClick={() => setShowDeleteConfirm(true)} className="flex-1 flex justify-center items-center gap-2 py-2 bg-red-100 cursor-pointer hover:bg-red-200 text-red-600">
                        <span className="text-sm">Xoá tất cả</span>
                        <FontAwesomeIcon icon={faTrash} />
                    </div>
                )}
            </div>

            {/* Delete confirmation modal */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowDeleteConfirm(false)}>
                    <div className="bg-surface rounded-lg p-4 mx-4 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
                        <h3 className="font-semibold text-lg mb-2">Xác nhận xoá</h3>
                        <p className="text-content-2 mb-4">Bạn có chắc muốn xoá tất cả {notifications.length} thông báo?</p>
                        <div className="flex gap-2">
                            <button onClick={() => setShowDeleteConfirm(false)} className="flex-1 py-2 border rounded-lg hover:bg-surface-muted">
                                Huỷ
                            </button>
                            <button onClick={onDeleteAll} disabled={deleting} className={`flex-1 py-2 rounded-lg text-white ${deleting ? "bg-red-300" : "bg-red-500 hover:bg-red-600"}`}>
                                {deleting ? "Đang xoá…" : "Xoá tất cả"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/** Item tách riêng để tối ưu re-render */
const NotiItem = React.memo(function NotiItem({ noti, onClick, onDelete }) {
    const colorClass = TYPE_COLORS[noti.type] || "text-content-2";
    const Icon = TYPE_ICONS[noti.type] || faNoteSticky;

    const [translateX, setTranslateX] = React.useState(0);
    const [isSwiping, setIsSwiping] = React.useState(false);
    const touchStartX = React.useRef(0);
    const DELETE_THRESHOLD = 50;

    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
        setIsSwiping(true);
    };

    const handleTouchMove = (e) => {
        if (!isSwiping) return;
        const currentX = e.touches[0].clientX;
        const diff = touchStartX.current - currentX;
        // Only allow swipe left (positive diff), limit to DELETE_THRESHOLD
        if (diff > 0) {
            setTranslateX(Math.min(diff, DELETE_THRESHOLD));
        } else {
            setTranslateX(0);
        }
    };

    const handleTouchEnd = () => {
        setIsSwiping(false);
        // Full swipe = delete immediately
        if (translateX >= DELETE_THRESHOLD) {
            if (onDelete && noti.id) onDelete(noti.id);
            return;
        }
        // Snap to open or closed position
        if (translateX > DELETE_THRESHOLD / 2) {
            setTranslateX(DELETE_THRESHOLD);
        } else {
            setTranslateX(0);
        }
    };

    const handleDelete = (e) => {
        e.stopPropagation();
        if (onDelete && noti.id) onDelete(noti.id);
    };

    const handleClick = () => {
        if (translateX > 0) {
            setTranslateX(0);
            return;
        }
        if (onClick) onClick(noti);
    };

    const handleKey = (e) => {
        if (e.key === "Enter" && onClick) onClick(noti);
    };

    return (
        <div className="relative overflow-hidden rounded-lg">
            {/* Delete button behind - only visible when swiping */}
            {translateX > 0 && (
                <div className="absolute right-0 top-0 bottom-0 w-[50px] bg-red-400 flex items-center justify-center text-white">
                    <button onClick={handleDelete} className="w-full h-full flex items-center justify-center">
                        <FontAwesomeIcon icon={faTrash} className="text-sm" />
                    </button>
                </div>
            )}

            {/* Main content */}
            <div
                role="button"
                tabIndex={0}
                onClick={handleClick}
                onKeyDown={handleKey}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                style={{ transform: `translateX(-${translateX}px)`, transition: isSwiping ? "none" : "transform 0.2s ease-out" }}
                className={`flex gap-2 justify-start border-b last:border-none py-2 bg-surface cursor-pointer ${noti.unread === false ? "opacity-60" : ""}`}
            >
                <div className={`text-3xl p-4 ${colorClass}`}>
                    <FontAwesomeIcon icon={Icon} />
                </div>

                <div className="text-sm flex flex-col flex-1 pr-2">
                    <p className="font-bold flex items-center gap-2">
                        {noti.title || "Thông báo"}
                        {noti.unread && (
                            <span className="inline-flex items-center gap-1 text-xs text-rose-600">
                                <FontAwesomeIcon icon={faCircle} className="text-[8px]" />
                                Mới
                            </span>
                        )}
                    </p>

                    <span className="whitespace-pre-line">{noti.message}</span>

                    {/* Thời gian tương đối; di chuột để xem mốc đầy đủ */}
                    <span className="text-sm text-content-3" title={formatFullDateTime(noti.createTime)}>{formatTimeAgo(noti.createTime)}</span>
                </div>
            </div>
        </div>
    );
});

export default Notification;
